import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Title,
  Text,
  LayoutCard,
  useComponentTheme,
  Image as ImageComponent,
  Col,
  Link,
  SkeletonLoader,
} from '@vmos2/design-system';
import { usePageLoading, useSettings } from '@vmos2/providers';
import { ConditionalWrapper } from '@vmos2/utils';
import styled from '@emotion/styled';
import isNaN from 'lodash/isNaN';
import { isEmpty } from 'lodash';

import type { LoyaltyData } from 'types';
import { StampCard } from '.';
import { RewardCounter } from '../rewardCounter';
import { RewardsPageComponents } from '../constants';

type Props = {
  buttonLinkComponent?: () => ComponentType<typeof Button>,
  componentInformation?: {
    name: string,
  },
  isFetchingLoyaltyData: boolean,
  isGuest?: boolean,
  isSmallBox?: boolean,
  loyaltyData?: LoyaltyData,
  mode: 'default' | 'Welcome:LoyaltyStamps' | 'Rewards:LoyaltyStamps',
  titleMode: 'default' | 'Welcome' | 'PanelHeader',
};

export const LoyaltyStamps = ({
  buttonLinkComponent: ButtonLink,
  componentInformation,
  isFetchingLoyaltyData,
  isGuest,
  isSmallBox,
  loyaltyData,
  mode,
  titleMode,
}: Props) => {
  const { loyaltyStampCard } = useSettings();
  const [isStampImageLoading, setIsStampImageLoading] = useState(true);
  const { isLoading, setComponentLoaded } = usePageLoading();
  const { t } = useTranslation('rewards.stamps');
  const { headerImage, illustrationImage } = useComponentTheme(theme => theme.components.LayoutCard, 'Stamps');
  const componentTheme = useComponentTheme(theme => theme.components.LayoutCard, mode);
  const rewardCounterMode = `${mode}:RewardCounter`;
  const rewardCounterComponentTheme = useComponentTheme(theme => theme.components.LayoutCard, rewardCounterMode);
  const pointsPerPromotion = loyaltyStampCard?.stampsPerPromo || 0;
  const calculatedNrOfStamps = Math.round((loyaltyData?.points || 0) % pointsPerPromotion);
  const numberOfStamps = isNaN(calculatedNrOfStamps) ? 0 : calculatedNrOfStamps;
  const stampCardImageUrl = loyaltyStampCard?.stampCardImages?.[numberOfStamps];
  const showLoyaltyStampsSection = loyaltyData?.enabled;

  useEffect(() => {
    if (!isFetchingLoyaltyData && !isStampImageLoading) {
      setComponentLoaded(componentInformation?.name || RewardsPageComponents.STAMPS);
    }
  }, [componentInformation?.name, isFetchingLoyaltyData, isStampImageLoading, setComponentLoaded]);

  if (!isLoading && (!loyaltyStampCard?.enabled || isGuest || isEmpty(loyaltyData) || !showLoyaltyStampsSection)) {
    return null;
  }

  const isDescriptionActive = !!t('description');
  const isTitleActive = !!t('title');
  const title = t('header');

  const loyaltyRewardCounter = (
    <ConditionalWrapper
      condition={isSmallBox}
      wrapper={children => (
        <LinkWrapper componentTheme={rewardCounterComponentTheme} to="profile.rewards">
          {children}
        </LinkWrapper>
      )}
    >
      <RewardCounter align="center" loyaltyData={loyaltyData} />
    </ConditionalWrapper>
  );

  return (
    <>
      <Col
        data-test="profile-stamps"
        isLoading={isLoading}
        mode={mode}
        tag={LayoutCard}
        title={
          title && (
            <Title
              dataTest="profile-stamps-title"
              mode={titleMode}
              {...(isSmallBox ? { mb: 4, level: 1 } : { mb: 6, mt: 0, level: 2 })}
            >
              {title}
            </Title>
          )
        }
        {...(!isSmallBox && { lg: 8, lgOffset: 2, md: 12 })}
      >
        <SkeletonLoader isLoading={isLoading}>
          <Content componentTheme={componentTheme} data-test="profile-stamps-content" isSmallBox={isSmallBox}>
            {!stampCardImageUrl && !!headerImage && (
              <ImageWrapper>
                <Img src={headerImage} />
              </ImageWrapper>
            )}

            <StampsGridWrapper>
              <StampCard setIsStampImageLoading={setIsStampImageLoading} userPoints={loyaltyData?.points} />
              {!stampCardImageUrl && !!illustrationImage && <IllustrationImage src={illustrationImage} />}
            </StampsGridWrapper>

            {!stampCardImageUrl && (isTitleActive || isDescriptionActive) && (
              <ContentText>
                {isTitleActive && (
                  <Title level={4} margin="small">
                    {t('title')}
                  </Title>
                )}

                {isDescriptionActive && <Description>{t('description')}</Description>}
              </ContentText>
            )}
          </Content>

          {!isSmallBox && (
            <Content
              componentTheme={rewardCounterComponentTheme}
              data-test="profile-stamps-content-reward-counter"
              isSmallBox={isSmallBox}
              showPadding
              showMarginTop
            >
              {loyaltyRewardCounter}
            </Content>
          )}

          {ButtonLink && (
            <ButtonCol mt={6}>
              <ButtonLink contentCenter size="small" mode="primary" as={Link} to="profile.rewards">
                {t('goToRewards')}
              </ButtonLink>
            </ButtonCol>
          )}
        </SkeletonLoader>
      </Col>

      {!isLoading && isSmallBox && (
        <Col
          data-test="profile-stamps-reward-counter"
          isSmallBox={isSmallBox}
          mode={rewardCounterMode}
          css={{ position: 'relative', marginTop: 'calc(var(--row-gap) * -1 + 16px)' }}
          tag={LayoutCard}
        >
          {loyaltyRewardCounter}
        </Col>
      )}
    </>
  );
};

const ButtonCol = styled(Col)`
  text-align: center;
`;

const Content = styled.div(
  ({ componentTheme: { backgroundColor }, isSmallBox, showMarginTop, showPadding, theme }) => `
    width: 100%;
    max-width: 327px;
    overflow: hidden;
    ${showPadding ? `padding: ${theme.margins.md} 0;` : ''}
    margin: ${showMarginTop ? theme.margins.md : 0} auto 0;
    ${!isSmallBox ? `border: ${theme.stroke.light};` : ''}
    border-radius: ${theme.borderRadius.large};
    ${backgroundColor ? `background-color: ${backgroundColor};` : ''}
  `
);

const ContentText = styled.div`
  margin-top: ${({ theme }) => theme.margins.xl};
`;

const Description = styled(Text)`
  font-size: ${({ theme }) => theme.fontSize.sm};
  white-space: pre-wrap;
`;

const IllustrationImage = styled(ImageComponent)`
  margin-left: ${({ theme }) => theme.margins.xxl};
  max-width: 120px;
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const Img = styled(ImageComponent)`
  max-width: 100%;
  max-height: 100px;
  margin-bottom: ${({ theme }) => theme.margins.xl};
`;

const LinkWrapper = styled(Link)`
  text-decoration: none;
`;

const StampsGridWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
